<!-- 
Chart showing exception counts for each month.
-->
<template>
  <div>
    <div v-if="noToolbar">
      <line-chart 
        v-if="!isChartEmpty(chartData)"
        ref="chart"
        :chart-data="chartData"
        :chart-options="chartOptions"
        style="height: 265px"
      />
      <our-chart-is-empty-placeholder v-if="isChartEmpty(chartData)" style="height: 265px" />
    </div>
    <v-flex v-else>
      <v-card>
        <our-widget-toolbar 
          fixed for-dialog 
          title="Exception counts by month" 
          @refresh="refresh"
        />
        <our-busy-indicator :show="!loaded"/>
        <v-card-text class="pb-3">
          <line-chart 
            v-if="!isChartEmpty(chartData)"
            ref="chart" 
            :chart-data="chartData" 
            :chart-options="chartOptions" 
            style="height: 265px"
          />
          <our-chart-is-empty-placeholder v-if="isChartEmpty(chartData)" style="height: 265px" />
        </v-card-text>
        <slot/>
      </v-card>
    </v-flex>
  </div>
</template>

<script>
import _ from "underscore";
import moment from "moment";
import * as helpers from "@/store/exception-logs/helpers";
import * as action from "@/store/exception-logs/action-types";
import * as mutation from "@/store/exception-logs/mutation-types";
import LineChart from "@/components/common/chart/LineChart.vue";
import chartMixins from "@/mixins/chart.mixins";

export default {
  components: { LineChart },
  mixins: [chartMixins],
  props: {
    noToolbar: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    chartOptions() {
      return {
        responsive: true,
        maintainAspectRatio: false,
        onClick: this.chartClick,
        onHover: this.chartHover,
        tooltips: {
          callbacks: {
            afterBody: this.getTooltipFooter
          }
        },
        scales: {
          yAxes: [{ ticks: { min: 0 }, afterBuildTicks: this.setYAxesTicks }]
        }
      };
    },
    state() {
      return this.$store.state.exceptionLogs.month;
    },
    loaded() {
      return this.state.loaded;
    },
    chartData() {
      if (!this.loaded) return {};
      const d = this.state.data;
      const labels = _(_.pluck(d, "monthStarting")).map(m => moment(m).format("MMM YYYY"));
      var keys = _.keys(d[0]);
      keys = _.without(keys, "monthStarting");
      keys = _.map(keys, level => level[0].toUpperCase() + level.substr(1));
      var datasets = [];
      keys.forEach((key, i) => {
        var dataset = {
          label: key,
          borderColor: helpers.ExceptionChartColors[key],
          backgroundColor: helpers.ExceptionChartColors[key] + `${(i + 1) * 10}`,
          fill: "origin"
        };
        dataset.data = _.pluck(d, key.toLowerCase());
        datasets.push(dataset);
      });
      return { labels, datasets };
    }
  },
  watch: {
    loaded() {
      this.loaded ? this.drawChart() : this.refresh();
    }
  },
  mounted() {
    this.loaded ? this.drawChart() : this.refresh();
  },
  methods: {
    async refresh() {
      await this.$store.dispatch(action.STATS_BY_MONTH);
      if (this.loaded) {
        this.drawChart();
      }
    },
    drawChart() {
      this.$nextTick(() => {
        if (this.$refs.chart) this.$refs.chart.display();
      });
    },
    chartClick: function(e) {
      const chartObject = this.$refs.chart.$data._chart;
      const activePoints = chartObject.getElementsAtXAxis(e);
      if (activePoints[0]) {
        const idx = activePoints[0]["_index"];
        if (this.getErrorCountByIndex(idx) > 0) {
          const start = moment(this.state.data[idx].monthStarting);
          const end = moment(this.state.data[idx].monthStarting);
          end.add(1, "months");
          end.subtract(1, "days");
          this.$store.commit(mutation.LIST_CLEAR_CRITERIA);
          this.$store.commit(mutation.LIST_SET_CRITERIA, {
            fromDate: start.format("YYYY-MM-DD"),
            toDate: end.format("YYYY-MM-DD")
          });
          this.$store.dispatch(action.LIST);
          this.$router.push("/exception-logs");
          this.$emit("click");
        }
      }
    },
    chartHover: function(e) {
      const chartObject = this.$refs.chart.$data._chart;
      const activePoints = chartObject.getElementsAtXAxis(e);
      if (activePoints[0]) {
        const idx = activePoints[0]["_index"];
        if (this.getErrorCountByIndex(idx) > 0) {
          chartObject.canvas.style.cursor = "pointer";
          return;
        }
      }
      chartObject.canvas.style.cursor = "default";
    },
    getTooltipFooter(tooltipItems) {
      const index = _(tooltipItems).first().index;
      return this.getErrorCountByIndex(index) > 0 ? ["", "(click to show)"] : null;
    },
    getErrorCountByIndex(index) {
      const d = this.state.data[index];
      return d.debug + d.error + d.fatal + d.info + d.trace + d.warn;
    },
    setYAxesTicks(axis) {
      return _(axis.ticks).filter(t => t == Math.trunc(t));
    }
  }
};
</script>
